import { DATE_FORMAT_ISO, format, parse } from '@eneco/common/date';
import { capitalizeFirstLetter } from '@eneco/common/string';
import useDC from '@eneco/dc/useDC';
import { addLeadingZero } from '@eneco/flows/helpers';
import { Offer, Product } from '@eneco/flows/types';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useGTM } from '@eneco/tracking';
import {
  DC_Domain_Models_Products_ProductType,
  Products_Offers_V3_OfferResponseModel,
  Products_Orders_SwitchTypeResponse,
} from '@monorepo-types/dc';
import { useLayoutData } from '@sitecore/common';

import { FlowContextServiceGemak } from '../context';

interface EcommerceEventItem {
  item_name: string; // product name
  item_id: string; // product code (back in a day called SKU)
  product_combination?: string; // product combination code (HP8xP0xA12xWRVxWTWKTWxWWSET)
  price: number; // original monthly amount / one-time price
  price_upsell_gas?: number; // upsell product gas specific price
  price_upsell_electricity?: number; // upsell product electricity specific price
  item_brand: string; // brand, capitalized "DC_Repositories_Base_Enumerations_Label"
  item_category: 'consumenten' | 'zakelijk'; // market type
  item_category2: 'commodity' | 'non-commodity'; // product type category
  item_category3: DC_Domain_Models_Products_ProductType; // product type
  item_category4: string; // order type // TODO: fix it, dc response not very well typed yet, should be "DC_Domain_Models_Products_OrdersV2_OrderType"
  customer_segment: string; // order action code
  item_variant: string; // contract duration
  item_list_name: string; // funnel name
  index: number; // product position in the list (first product should have value 0)
  quantity: number; // quantity of the product, must be an integer
}

interface EcommerceEventViewItem {
  event: 'view_item';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventViewCart {
  event: 'view_cart';
  ecommerce: {
    value: number; // total order value (per month, tax included)
    currency: 'EUR';
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventAddToCart {
  event: 'add_to_cart';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventRemoveFromCart {
  event: 'remove_from_cart';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventAddToWishlist {
  event: 'add_to_wishlist';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}

interface EcommerceEventBeginCheckout {
  event: 'begin_checkout';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}

interface EcommerceEventPurchase {
  event: 'purchase';
  contract_startdate: string; // start date of the contract, format: "DD-MM-YYYY"
  internalcampaign_id: string; // internal campaign id
  internalcampaign_name: string; // internal campaign name
  ecommerce: {
    transaction_id: string; // unique identifier for the order / transaction
    currency: 'EUR';
    coupon?: string;
    value: number; // total order value (per month, tax included)
    tax: number; // tax included in the total order value
    items: EcommerceEventItem[];
  };
}

type Event =
  | EcommerceEventViewItem
  | EcommerceEventViewCart
  | EcommerceEventAddToCart
  | EcommerceEventRemoveFromCart
  | EcommerceEventAddToWishlist
  | EcommerceEventBeginCheckout
  | EcommerceEventPurchase;

export const useFlowTracking = () => {
  const { label } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowContextServiceGemak>();
  const { pushToDataLayer } = useGTM();

  const isSME = useFlowSelector(state => state.context.isSME);
  const voucherCode = useFlowSelector(state => state.context.voucherCode);
  const basketId = useFlowSelector(state => state.context.basketId);

  const {
    route: { fields = {} },
  } = useLayoutData();

  const pushEvent = (event: Event, isEcommerce?: boolean) => {
    // every Ecommerce event should be preceded by this
    if (isEcommerce) pushToDataLayer({ ecommerce: null });

    // JSON stringify && parse to filter out properties with undefined values
    pushToDataLayer(JSON.parse(JSON.stringify(event)));
  };

  const mapOfferProductsToEventItems = (
    selectedProducts: Product[],
    dcOffer?: Products_Offers_V3_OfferResponseModel | undefined, // contains more order related inforamtion such is discount / vouchers and etc
  ): EcommerceEventItem[] =>
    selectedProducts?.map((product, index) => {
      const itemName = (product.type && capitalizeFirstLetter(product.type.toLowerCase())) || 'n/a';
      return {
        item_name: itemName,
        item_id: product.code,
        product_combination: product.productCombinationCode ?? 'n/a',
        item_brand: capitalizeFirstLetter(label),
        item_list_name: fields.flowName?.value?.toString() ?? 'n/a',
        price: Number(product.costsMonthly?.vatIncluded),
        discount: 0,
        item_category: isSME ? 'zakelijk' : 'consumenten',
        item_category2: 'non-commodity',
        item_category3: product.type,
        item_category4: 'n/a',
        customer_segment: dcOffer?.configuration?.actionCode ?? 'n/a',
        item_variant: product.variant ?? 'n/a',
        index,
        quantity: 1, // TODO: fill in dynamicly
      };
    });

  const trackViewItem = (
    viewedOffer: Omit<Offer, 'mostPopularChoice'>,
    dcOffer?: Products_Offers_V3_OfferResponseModel | undefined,
  ): void => {
    const event: EcommerceEventViewItem = {
      event: 'view_item',
      ecommerce: {
        items: mapOfferProductsToEventItems(viewedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackViewCart = (selectedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel | undefined): void => {
    const event: EcommerceEventViewCart = {
      event: 'view_cart',
      ecommerce: {
        value: Number(selectedOffer.costsMonthly?.vatIncluded),
        currency: 'EUR',
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };
    pushEvent(event, true);
  };

  const trackRemoveFromCart = (
    selectedOffer: Omit<Offer, 'mostPopularChoice'>,
    dcOffer?: Products_Offers_V3_OfferResponseModel | undefined,
  ): void => {
    const event: EcommerceEventRemoveFromCart = {
      event: 'remove_from_cart',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };
  const trackAddToCart = (
    selectedOffer: Omit<Offer, 'mostPopularChoice'>,
    dcOffer?: Products_Offers_V3_OfferResponseModel | undefined,
  ): void => {
    const event: EcommerceEventAddToCart = {
      event: 'add_to_cart',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackAddToWishList = (
    selectedOffer: Omit<Offer, 'mostPopularChoice'>,
    dcOffer?: Products_Offers_V3_OfferResponseModel | undefined,
  ): void => {
    const event: EcommerceEventAddToWishlist = {
      event: 'add_to_wishlist',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackBeginCheckout = (
    selectedOffer: Omit<Offer, 'mostPopularChoice'>,
    dcOffer?: Products_Offers_V3_OfferResponseModel | undefined,
  ): void => {
    const event: EcommerceEventBeginCheckout = {
      event: 'begin_checkout',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackPurchase = (
    selectedOffer: Omit<Offer, 'mostPopularChoice'>,
    dcOffer?: Products_Offers_V3_OfferResponseModel | undefined,
    switchTypes?: Array<Products_Orders_SwitchTypeResponse>,
  ): void => {
    // TODO: fix deprecation warning, here we need a date formatter
    // that works outside of a react components (not a hook)
    const contractStartDate = format(parse(selectedOffer?.contractStartDate ?? '', DATE_FORMAT_ISO))?.split('-');

    const event: EcommerceEventPurchase = {
      event: 'purchase',
      contract_startdate: `${addLeadingZero(contractStartDate?.[0] ?? '')}-${addLeadingZero(
        contractStartDate?.[1] ?? '',
      )}-${contractStartDate?.[2]}`,
      internalcampaign_id: '',
      internalcampaign_name: '',
      ecommerce: {
        transaction_id: basketId ?? '',
        value: Number(selectedOffer.costsMonthly?.vatIncluded),
        tax: Number(selectedOffer.costsMonthly?.vatAmount),
        currency: 'EUR',
        coupon: voucherCode ?? '',
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer).map(product => {
          // add product specific properties
          product.item_category4 =
            switchTypes?.find(type => type.productType?.toLowerCase() === product.item_category3)?.orderType ?? '';

          return product;
        }),
      },
    };

    pushEvent(event, true);
  };

  return {
    trackViewItem,
    trackViewCart,
    trackAddToCart,
    trackRemoveFromCart,
    trackAddToWishList,
    trackBeginCheckout,
    trackPurchase,
  };
};
